import { useState, useRef, useCallback } from "react";
import { message } from "antd";
import { EventSourcePolyfill } from "event-source-polyfill";
import { END_POINT } from "config/environment";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { Item } from "../../../types";

export const useFileProcessing = (
  checkDocumentRequiresOCR: (file: File) => Promise<boolean>,
  addOcrErrorLog: (fileName: string, reason?: string) => void,
) => {
  const { token } = useGetUserInfo();
  const processingEventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 6;

  const [processingItems, setProcessingItems] = useState<Item[]>([]);
  const [processingStatus, setProcessingStatus] = useState<{
    [key: number]: string;
  }>({});
  const [progress, setProgress] = useState<{ [key: number]: number }>({});

  const generateUUID = (): string =>
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  const generateChatId = () => generateUUID();

  const startSSEForProcessing = useCallback(
    (chatId: string, itemId: any, onComplete?: () => void) => {
      if (processingEventSourceRef.current) {
        processingEventSourceRef.current.close();
        processingEventSourceRef.current = null;
      }

      const eventSource = new EventSourcePolyfill(
        `${END_POINT.BASE_URL}/v1/processing-updates/${chatId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          heartbeatTimeout: 300000,
          withCredentials: false,
        },
      );

      processingEventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case "processing_update": {
              const { processedCvs, totalCvs } = data.data;
              const total = Number(totalCvs) || 0;
              const processed = Number(processedCvs) || 0;
              const pct =
                total > 0 ? Math.min(95, 10 + (processed / total) * 80) : 10;
              setProgress((prev) => ({ ...prev, [itemId]: pct }));
              setProcessingStatus((prev) => ({
                ...prev,
                [itemId]:
                  total > 0
                    ? processed >= total
                      ? `Processed ${processed} of ${total} CVs — saving results…`
                      : `Processed ${processed} of ${total} CVs`
                    : "Processing CVs…",
              }));
              break;
            }

            case "processing_complete":
              retryCountRef.current = 0;
              setProcessingStatus((prev) => ({
                ...prev,
                [itemId]: "Processing completed!",
              }));
              setProgress((prev) => ({ ...prev, [itemId]: 100 }));
              processingEventSourceRef.current?.close();
              processingEventSourceRef.current = null;
              setProcessingItems((prev) =>
                prev.filter((item) => item.id !== itemId),
              );
              message.success("Processing completed successfully!");
              onComplete?.();
              break;

            case "processing_timeout":
              setProcessingStatus((prev) => ({
                ...prev,
                [itemId]:
                  "Processing timeout - please refresh to check results",
              }));
              setProgress((prev) => ({ ...prev, [itemId]: 0 }));
              message.warning(
                "Processing is taking longer than expected. Please refresh to check results.",
              );
              processingEventSourceRef.current?.close();
              processingEventSourceRef.current = null;
              setProcessingItems((prev) =>
                prev.filter((item) => item.id !== itemId),
              );
              onComplete?.();
              break;

            case "error":
              console.error("SSE Error:", data.data);
              message.error("An error occurred during processing.");
              setProcessingStatus((prev) => ({
                ...prev,
                [itemId]: "Processing failed",
              }));
              setProgress((prev) => ({ ...prev, [itemId]: 0 }));
              processingEventSourceRef.current?.close();
              processingEventSourceRef.current = null;
              onComplete?.();
              break;
          }
        } catch (err) {
          console.error("Error parsing SSE data:", err);
          message.error("Error receiving processing updates.");
        }
      };

      eventSource.onerror = () => {
        processingEventSourceRef.current?.close();
        processingEventSourceRef.current = null;

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          console.warn(
            `SSE connection lost. Retrying (${retryCountRef.current}/${MAX_RETRIES})...`,
          );
          setTimeout(() => {
            startSSEForProcessing(chatId, itemId, onComplete);
          }, 3000 * retryCountRef.current);
          return;
        }

        retryCountRef.current = 0;
        setProcessingStatus((prev) => ({
          ...prev,
          [itemId]: "Connection error - refresh to check status",
        }));
        setProgress((prev) => ({ ...prev, [itemId]: 0 }));
        message.error(
          "Lost connection to server. Please refresh to see results.",
        );
        setProcessingItems((prev) => prev.filter((item) => item.id !== itemId));
        onComplete?.();
      };

      return () => {
        processingEventSourceRef.current?.close();
        processingEventSourceRef.current = null;
      };
    },
    [token],
  );

  return {
    processingItems,
    setProcessingItems,
    processingStatus,
    setProcessingStatus,
    progress,
    setProgress,
    generateChatId,
    startSSEForProcessing,
  };
};
